import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, BACKEND_URL, formatAud } from "../../lib/api";

// Proxy Pinnacle images through backend (avoids auth on img src)
const proxyImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http://api.carparts-au.com/")) {
    return `${BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

// Human-readable category from Pinnacle itemTypeCode
const formatCategory = (code) => {
  if (!code) return "";
  return code.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Search, Star, Eye, EyeOff, ShoppingCart, Image as ImgIcon,
  Pencil, Facebook, Instagram, Tag, Percent, X, ChevronDown, AlertTriangle,
  Plus as PlusIcon, History, Trash2, Clock, DollarSign, Package, Zap, RefreshCcw,
  ChevronLeft, ChevronRight, ZoomIn, Car, MapPin, Shield, Phone, Globe, Building2, Receipt,
} from "lucide-react";
import SyncBadge from "../../components/SyncBadge";
import VehicleStockView from "../../components/VehicleStockView";
import { toast } from "sonner";

const PRESET_TAGS = ["New Arrival", "Clearance", "Hot Deal", "Last One", "Low Stock", "Featured"];

/* Light-theme tag badge styles */
const TAG_STYLES = {
  "New Arrival": "bg-blue-100 text-blue-700 border-blue-200",
  "Clearance":   "bg-red-100 text-red-700 border-red-200",
  "Hot Deal":    "bg-orange-100 text-orange-700 border-orange-200",
  "Last One":    "bg-purple-100 text-purple-700 border-purple-200",
  "Low Stock":   "bg-amber-100 text-amber-700 border-amber-200",
  "Featured":    "bg-green-100 text-green-700 border-green-200",
};

const EbayIcon = ({ className = "h-3 w-3" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M2.5 11.6h2.04c-.04-.85-.61-1.36-1.4-1.36-.95 0-1.42.6-1.46 1.36zm8.43-.84c-.83-.49-2.27-.41-2.99.27 0 .43.31.85 1.27.85.85 0 1.51-.46 1.72-1.12zM23 9.9 21.4 14l-1.45-4.1H18.4l1.99 4.97-.65 1.6h1.49L23 9.9zm-7.66 1.83c-.04-.83-.65-1.32-1.42-1.32-.85 0-1.42.5-1.5 1.32h2.92zm-1.46 2.5c1.04 0 1.69-.46 1.92-1.27l-1.13-.18c-.13.42-.45.62-.79.62-.5 0-.85-.36-.86-.95h2.84v-.27c0-1.5-.96-2.45-2.31-2.45-1.43 0-2.39 1-2.39 2.5 0 1.45.95 2 2.72 2zM7.21 9.86c-1.49 0-2.31.94-2.31 2.5 0 1.36.91 2.5 2.42 2.5.79 0 1.45-.36 1.71-.95l-1.13-.18c-.16.34-.46.55-.85.55-.49 0-.83-.34-.85-.95h2.86l.04-.27c0-1.59-.86-2.45-2.31-2.45zM3.13 14.86c.96 0 1.62-.45 1.92-1.18l-1.13-.18c-.16.31-.45.5-.79.5-.55 0-.96-.42-.96-1.05v-.05h2.84v-.27c0-1.5-.99-2.41-2.36-2.41-1.36 0-2.31.96-2.31 2.5 0 1.41.95 2.14 2.41 2.14zm9.81 0c.83 0 1.4-.31 1.7-.78v.62h1.18v-3c0-1.36-.81-1.85-2.13-1.85-1.13 0-1.91.46-2.04 1.36l1.18.13c.07-.36.34-.55.86-.55.62 0 .91.18.91.5v.18l-1.45.18c-1.31.13-1.95.62-1.95 1.45 0 .85.65 1.36 1.74 1.36z"/>
  </svg>
);

const PAGE_SIZE = 50;

export default function AdminProducts() {
  const [mainTab, setMainTab] = useState("parts"); // "parts" | "vehicles"
  const [items,       setItems]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [activeTotal, setActiveTotal] = useState(0);
  const [grandTotal,  setGrandTotal]  = useState(0);
  // Read URL params so links from other pages (e.g. order detail) can pre-set filters
  const urlParams = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    q:            urlParams.get("q")            || "",
    status:       "all",
    activeStatus: urlParams.get("activeStatus") || "active",
    zeroPrice:    urlParams.get("zeroPrice") === "true",
  });
  const [selected,    setSelected]    = useState(new Set());
  const [bulkOpen,    setBulkOpen]    = useState(false);
  const [bulkOrderOpen, setBulkOrderOpen] = useState(false);
  const [ebayOpen,    setEbayOpen]    = useState(null);
  const [fbOpen,      setFbOpen]      = useState(null);
  const [hideConfirm, setHideConfirm] = useState(null);
  const [tagProduct,  setTagProduct]  = useState(null);
  const [page,        setPage]        = useState(1);
  const [detailProduct, setDetailProduct] = useState(null);

  const load = async () => {
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (filters.q) params.set("q", filters.q);
    if (filters.activeStatus === "active")   params.set("is_active", "true");
    else if (filters.activeStatus === "inactive") params.set("is_active", "false");
    // activeStatus === "all" → omit is_active entirely, backend returns everything
    if (filters.zeroPrice) params.set("zero_price", "true");
    const { data } = await adminApi.get(`/admin/products?${params.toString()}`);
    let list = data.items || [];
    if (filters.status === "hidden")     list = list.filter((p) => p.is_hidden);
    else if (filters.status === "featured")   list = list.filter((p) => p.is_featured);
    else if (filters.status === "ebay")       list = list.filter((p) => p.ebay_listing?.status === "listed");
    else if (filters.status === "negotiable") list = list.filter((p) => p.is_negotiable);
    setItems(list);
    setTotal(data.total || 0);
    setActiveTotal(data.active_total || 0);
    setGrandTotal(data.grand_total || 0);
  };

  useEffect(() => {
    adminApi.get("/products/facets").then((r) => {
    });
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, page]);

  const toggle    = (id) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); setSelected(n); };
  const toggleAll = () => { setSelected(selected.size === items.length ? new Set() : new Set(items.map((p) => p.id))); };

  const ebayProduct = items.find((p) => p.id === ebayOpen);
  const fbProduct   = items.find((p) => p.id === fbOpen);

  const confirmHide = async () => {
    const p = hideConfirm;
    await adminApi.post("/admin/products/bulk", { ids: [p.id], action: p.is_hidden ? "unhide" : "hide" });
    toast.success(p.is_hidden ? "Product re-listed on website" : "Product hidden from website");
    setHideConfirm(null);
    load();
  };

  const inputStyle = { background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" };
  const selectStyle = { background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" };

  const TabSwitcher = (
    <div className="inline-flex items-center gap-1 bg-white rounded-2xl p-1.5 shadow-sm" style={{ border: "1px solid #dde3ef" }} data-testid="inventory-main-tabs">
      <button onClick={() => setMainTab("parts")}
        className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors"
        style={{ background: mainTab === "parts" ? "#1e5bb8" : "transparent", color: mainTab === "parts" ? "#fff" : "#64748b" }}
        data-testid="tab-part-stock">
        Part Stock
      </button>
      <button onClick={() => setMainTab("vehicles")}
        className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors"
        style={{ background: mainTab === "vehicles" ? "#1e5bb8" : "transparent", color: mainTab === "vehicles" ? "#fff" : "#64748b" }}
        data-testid="tab-vehicle-stock">
        Vehicle Stock
      </button>
    </div>
  );

  if (mainTab === "vehicles") {
    return (
      <div className="space-y-5" data-testid="admin-products-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h1 className="font-heading text-[28px] font-bold" style={{ color: "#0f172a" }}>Inventory</h1>
          {TabSwitcher}
        </div>
        <VehicleStockView onOpenPart={(productId) => { setMainTab("parts"); setDetailProduct(productId); }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-products-page">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color: "#0f172a" }}>
            Inventory
            <span className="ml-2 text-[18px] font-medium" style={{ color: "#94a3b8" }}>({total})</span>
          </h1>
          <p className="text-[12px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "#64748b" }}>
            <span>Live catalogue from Pinnacle · select rows for bulk actions</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#16a34a" }} />
              <span className="font-mono font-semibold" style={{ color: "#15803d" }}>{activeTotal.toLocaleString()} active</span>
            </span>
            {grandTotal > activeTotal && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <span className="font-mono" style={{ color: "#94a3b8" }}>{(grandTotal - activeTotal).toLocaleString()} inactive</span>
              </span>
            )}
            <span className="font-mono" style={{ color: "#cbd5e1" }}>· {grandTotal.toLocaleString()} total ever synced</span>
          </p>
        </div>
        {TabSwitcher}
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-mono font-bold" style={{ color: "#d97706" }} data-testid="bulk-selected-count">
              {selected.size} selected
            </span>
            <Button onClick={() => setBulkOrderOpen(true)}
              className="rounded-xl text-white font-semibold text-[13px]"
              style={{ background: "#1e5bb8" }}
              data-testid="bulk-order-open-btn">
              <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
              Add to Order
            </Button>
            <Button onClick={() => setBulkOpen(true)}
              className="rounded-xl text-white font-semibold text-[13px]"
              style={{ background: "#e31e24" }}
              data-testid="bulk-action-open-btn">
              Bulk Actions
            </Button>
            <Button variant="ghost" onClick={() => setSelected(new Set())}
              className="rounded-xl text-[13px]" style={{ color: "#64748b" }}>
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}
        data-testid="inventory-filters">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94a3b8" }} />
          <Input value={filters.q}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, q: e.target.value })); }}
            placeholder="Search by part name, SKU, make, model, year, category, OEM number…"
            className="pl-9 rounded-xl h-11 text-[14px]" style={inputStyle}
            data-testid="admin-products-search" />
          {filters.q && (
            <button onClick={() => { setPage(1); setFilters((f) => ({ ...f, q: "" })); }}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: "#94a3b8" }}>
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Active/Inactive toggle — controls which products from the database are shown */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Showing:</span>
        {[
          { v: "active",   label: "Active Only",   color: "#16a34a" },
          { v: "inactive", label: "Inactive Only", color: "#94a3b8" },
          { v: "all",      label: "Everything",    color: "#1e5bb8" },
        ].map(({ v, label, color }) => (
          <button key={v} onClick={() => setFilters((f) => ({ ...f, activeStatus: v, zeroPrice: false }))}
            data-testid={`filter-active-${v}`}
            className="px-3 h-7 text-[11px] font-mono font-semibold uppercase tracking-wide rounded-lg border transition-all"
            style={filters.activeStatus === v && !filters.zeroPrice
              ? { background: color, borderColor: color, color: "#ffffff" }
              : { background: "#f5f7fb", borderColor: "#dde3ef", color: "#475569" }
            }>
            {label}
          </button>
        ))}
        {/* Zero price filter */}
        <button
          onClick={() => setFilters((f) => ({ ...f, zeroPrice: !f.zeroPrice, activeStatus: "all" }))}
          className="px-3 h-7 text-[11px] font-mono font-semibold uppercase tracking-wide rounded-lg border transition-all"
          style={filters.zeroPrice
            ? { background: "#e31e24", borderColor: "#e31e24", color: "#ffffff" }
            : { background: "#f5f7fb", borderColor: "#dde3ef", color: "#475569" }
          }>
          💲 $0 Price
        </button>
      </div>

      {/* Status filter chips — includes Negotiable */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { v: "all",        label: "All",        color: null },
          { v: "featured",   label: "Featured",   color: "#d97706" },
          { v: "ebay",       label: "Listed on eBay", color: "#854d0e" },
          { v: "negotiable", label: "💬 Make Offer On", color: "#0f766e" },
          { v: "hidden",     label: "Hidden",     color: "#64748b" },
        ].map(({ v, label, color }) => (
          <button key={v} onClick={() => setFilters((f) => ({ ...f, status: v }))}
            data-testid={`filter-status-${v}`}
            className="px-3 h-7 text-[11px] font-mono font-semibold uppercase tracking-wide rounded-lg border transition-all"
            style={filters.status === v
              ? { background: color || "#e31e24", borderColor: color || "#e31e24", color: "#ffffff" }
              : { background: "#f5f7fb", borderColor: "#dde3ef", color: "#475569" }
            }>
            {label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm overflow-x-auto" style={{ border: "1px solid #dde3ef" }}>
        <table className="w-full text-sm" style={{ minWidth: 900 }}>
          <thead>
            <tr style={{ background: "#f5f7fb", borderBottom: "1px solid #dde3ef" }}>
              <th className="text-center px-3 py-3" style={{ width: 40 }}>
                <input type="checkbox"
                  checked={items.length > 0 && selected.size === items.length}
                  onChange={toggleAll}
                  data-testid="bulk-select-all" />
              </th>
              {[
                { label: "Product",    align: "left",   w: 220 },
                { label: "SKU",        align: "left",   w: 110 },
                { label: "Vehicle",    align: "left",   w: 140 },
                { label: "Price",      align: "right",  w: 85  },
                { label: "Stock",      align: "right",  w: 65  },
                { label: "Social",     align: "center", w: 90  },
                { label: "Status",     align: "left",   w: 180 },
                { label: "Sync",       align: "left",   w: 90  },
                { label: "Actions",    align: "right",  w: 140 },
              ].map(({ label, align, w }) => (
                <th key={label}
                  className="font-mono font-semibold uppercase py-3 px-3"
                  style={{ color: "#475569", fontSize: 10, letterSpacing: "0.15em", textAlign: align, width: w }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <Row key={p.id} p={p}
                checked={selected.has(p.id)}
                onToggle={() => toggle(p.id)}
                onEbay={() => setEbayOpen(p.id)}
                onFacebook={() => setFbOpen(p.id)}
                onHide={() => setHideConfirm(p)}
                onTag={() => setTagProduct(p)}
                onDetail={() => setDetailProduct(p.id)}
                onChanged={load} />
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={10} className="p-12 text-center font-mono uppercase tracking-widest text-[12px]"
                  style={{ color: "#94a3b8" }}>
                  No products match these filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[12px] font-mono" style={{ color: "#64748b" }}>
        <div>{items.length} shown · {total} total</div>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={page === 1} onClick={() => setPage((n) => n - 1)}
            className="rounded-lg text-[12px]"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Prev
          </Button>
          <div className="px-3">Page {page}</div>
          <Button size="sm" disabled={items.length < PAGE_SIZE} onClick={() => setPage((n) => n + 1)}
            className="rounded-lg text-[12px]"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Next
          </Button>
        </div>
      </div>

      {/* Modals */}
      {bulkOpen && (
        <BulkActionModal
          ids={Array.from(selected)}
          products={items.filter((p) => selected.has(p.id))}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setSelected(new Set()); load(); }}
        />
      )}
      {bulkOrderOpen && (
        <BulkOrderModal
          products={items.filter((p) => selected.has(p.id))}
          onClose={() => setBulkOrderOpen(false)}
          onCreated={() => { setBulkOrderOpen(false); setSelected(new Set()); load(); }}
        />
      )}
      {ebayOpen && ebayProduct && (
        <EbayModal product={ebayProduct} onClose={() => setEbayOpen(null)} onDone={() => { setEbayOpen(null); load(); }} />
      )}
      {fbOpen && fbProduct && (
        <FacebookModal product={fbProduct} onClose={() => setFbOpen(null)} onDone={() => { setFbOpen(null); load(); }} />
      )}
      {hideConfirm && (
        <ConfirmModal
          title={hideConfirm.is_hidden ? "Show on website?" : "Hide from website?"}
          message={hideConfirm.is_hidden
            ? `${hideConfirm.name} will be visible to customers again.`
            : `This will hide ${hideConfirm.name} from the customer website. Inventory data stays intact in Pinnacle. Continue?`}
          confirmLabel={hideConfirm.is_hidden ? "Show" : "Hide"}
          onClose={() => setHideConfirm(null)}
          onConfirm={confirmHide}
        />
      )}
      {tagProduct && (
        <TagModal
          product={tagProduct}
          onClose={() => setTagProduct(null)}
          onChanged={() => { setTagProduct(null); load(); }}
        />
      )}

      {/* ── Product Detail Drawer ─────────────────────────────────── */}
      {detailProduct && (
        <ProductDetailDrawer
          productId={detailProduct}
          onClose={() => setDetailProduct(null)}
          onOrderCreated={() => { setDetailProduct(null); toast.success("Order created — check Orders tab"); }}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ── Product Detail Drawer ──────────────────────────────────────────────
/* ── Image Selection Panel ─────────────────────────────────────────────
   Lets admin pick which Pinnacle images show to customers.
   ONLY writes featured_image + gallery_images — never touches sync fields.
   ─────────────────────────────────────────────────────────────────── */
function ImageSelectionPanel({ productId, images, featuredImage, galleryImages, proxyImg, adminApi, toast, onSaved }) {
  const [featured, setFeatured] = useState(featuredImage || "");
  const [gallery,  setGallery]  = useState(galleryImages || []);
  const [saving,   setSaving]   = useState(false);
  const [open,     setOpen]     = useState(false);

  const toggleGallery = (url) => {
    setGallery(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/products/${productId}/image-selection`, {
        featured_image: featured || null,
        gallery_images: gallery,
      });
      toast.success("Image selection saved");
      onSaved(featured || null, gallery);
    } catch(e) {
      toast.error("Failed to save image selection");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/products/${productId}/image-selection`, {
        featured_image: null,
        gallery_images: [],
      });
      setFeatured("");
      setGallery([]);
      toast.success("Reset to default — all Pinnacle images will show");
      onSaved(null, []);
    } catch { toast.error("Reset failed"); } finally { setSaving(false); }
  };

  const hasCustom = !!featured || gallery.length > 0;

  return (
    <div style={{ borderTop: "1px solid #eef2f8", background: "#fff" }}>
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5"
        style={{ background: hasCustom ? "#eff6ff" : "#f8fafc" }}>
        <div className="flex items-center gap-2">
          <ImgIcon className="h-3.5 w-3.5" style={{ color: hasCustom ? "#1e5bb8" : "#94a3b8" }}/>
          <span className="text-[11px] font-semibold" style={{ color: hasCustom ? "#1e40af" : "#64748b" }}>
            {hasCustom ? "Custom images selected" : "Select customer-facing images"}
          </span>
        </div>
        <span className="text-[10px] font-mono" style={{ color: "#94a3b8" }}>
          {open ? "▲ Hide" : "▼ Open"}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div className="text-[10px] font-mono leading-relaxed pt-1" style={{ color: "#64748b" }}>
            ⭐ = Main thumbnail (shop/home page) · ✓ = Include in gallery · Leave all unselected to show all images (default)
          </div>

          <div className="grid grid-cols-3 gap-2">
            {images.map((img, i) => {
              const isFeatured = featured === img;
              const inGallery  = gallery.includes(img);
              return (
                <div key={i} className="relative rounded-lg overflow-hidden"
                  style={{ border: isFeatured ? "2px solid #1e5bb8" : inGallery ? "2px solid #86efac" : "2px solid #e2e8f0" }}>
                  <img src={proxyImg(img)} alt="" className="w-full aspect-square object-cover" />
                  {/* Feature star */}
                  <button
                    onClick={() => setFeatured(isFeatured ? "" : img)}
                    title="Set as main thumbnail"
                    className="absolute top-1 left-1 w-6 h-6 rounded-full flex items-center justify-center text-[11px] shadow"
                    style={{ background: isFeatured ? "#1e5bb8" : "rgba(255,255,255,0.9)", color: isFeatured ? "#fff" : "#94a3b8" }}>
                    ⭐
                  </button>
                  {/* Gallery checkbox */}
                  <button
                    onClick={() => toggleGallery(img)}
                    title="Include in gallery"
                    className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-[11px] shadow"
                    style={{ background: inGallery ? "#16a34a" : "rgba(255,255,255,0.9)", color: inGallery ? "#fff" : "#94a3b8" }}>
                    ✓
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={saving}
              className="flex-1 py-2 rounded-xl text-[12px] font-bold transition-all disabled:opacity-50"
              style={{ background: "#0b1a3b", color: "#fff" }}>
              {saving ? "Saving…" : "Save Selection"}
            </button>
            {hasCustom && (
              <button onClick={reset} disabled={saving}
                className="px-3 py-2 rounded-xl text-[12px] font-semibold"
                style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#64748b" }}>
                Reset to default
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProductDetailDrawer({ productId, onClose, onOrderCreated, onChanged }) {
  const navigate = useNavigate();
  const [product,   setProduct]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [imgIdx,    setImgIdx]    = useState(0);
  const [lightbox,  setLightbox]  = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.get(`/admin/products/${productId}/detail`);
      setProduct(data);
      setImgIdx(0);
    } catch { toast.error("Could not load product"); onClose(); }
    finally { setLoading(false); }
  }, [productId, onClose]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") { if (lightbox) setLightbox(false); else onClose(); } };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, onClose]);

  const patchFlag = async (field, value) => {
    const action = field === "is_featured" ? (value ? "feature" : "unfeature")
      : field === "is_hidden" ? (value ? "hide" : "unhide")
      : field === "is_negotiable" ? (value ? "negotiable" : "unnegotiable")
      : null;
    if (!action) return;
    await adminApi.post("/admin/products/bulk", { ids: [product.id], action });
    setProduct(prev => ({ ...prev, [field]: value }));
    toast.success(`Updated`);
    onChanged();
  };

  const [restocking, setRestocking] = useState(false);
  const restock = async () => {
    setRestocking(true);
    try {
      await adminApi.post(`/admin/products/${product.id}/restock`);
      setProduct(prev => ({ ...prev, stock: 1, is_active: true, is_hidden: false }));
      toast.success("Marked back in stock");
      onChanged();
    } catch (e) {
      toast.error("Couldn't restock — try again");
    } finally {
      setRestocking(false);
    }
  };

  const images = product?.images?.length ? product.images : product?.image_url ? [product.image_url] : [];
  const hasImages = images.length > 0;
  const prevImg = () => setImgIdx(i => (i - 1 + images.length) % images.length);
  const nextImg = () => setImgIdx(i => (i + 1) % images.length);

  const condColor = !product ? {} :
    product.condition === "used"    ? { bg: "#dbeafe", fg: "#1e40af" } :
    product.condition === "damaged" ? { bg: "#fee2e2", fg: "#991b1b" } :
                                      { bg: "#dcfce7", fg: "#15803d" };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full z-50 flex flex-col bg-white shadow-2xl"
        style={{ width: "78%", maxWidth: 1040, borderLeft: "1px solid #dde3ef" }}>

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 shrink-0"
          style={{ borderBottom: "1px solid #eef2f8", background: "#0b1a3b" }}>
          <div className="flex items-center gap-3 min-w-0">
            {product && (
              <>
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                  {formatCategory(product.category)}
                </span>
                <span className="text-white/30">·</span>
                <span className="font-heading text-[16px] font-bold text-white truncate">{product.name}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {product && !product.is_deleted && (
              <>
                <Button onClick={() => navigate("/admin/price-quotes", {
                    state: { prefill: {
                      items: [{
                        source: "inventory",
                        product_id: product.id,
                        sku: product.sku,
                        name: product.name,
                        vehicle_make: product.vehicle_make || "",
                        vehicle_model: product.vehicle_model || "",
                        vehicle_year: product.vehicle_year || "",
                        stock_no: product.stock_no || "",
                        price: product.price || 0,
                        notes: "",
                      }],
                    } },
                  })}
                  variant="outline"
                  className="rounded-xl font-semibold text-[12px] h-8"
                  style={{ background: "rgba(255,255,255,0.1)", color: "#fff", borderColor: "rgba(255,255,255,0.2)" }}
                  data-testid="drawer-generate-quote">
                  <Receipt className="h-3.5 w-3.5 mr-1.5" />
                  Generate Quote
                </Button>
                <Button onClick={() => setOrderOpen(true)}
                  className="rounded-xl font-semibold text-[12px] h-8"
                  style={{ background: "#e31e24", color: "#fff" }}
                  disabled={product.stock <= 0}>
                  <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
                  Create Order
                </Button>
              </>
            )}
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-[12px] font-mono uppercase tracking-widest animate-pulse" style={{ color: "#94a3b8" }}>
              Loading…
            </span>
          </div>
        ) : product ? (
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-5 h-full">

              {/* ── Left: image gallery ──────────────────────────── */}
              <div className="col-span-2 flex flex-col" style={{ borderRight: "1px solid #eef2f8", background: "#f8fafc" }}>
                {/* Main image */}
                <div className="relative flex-1 flex items-center justify-center min-h-0" style={{ background: "#f0f4f8" }}>
                  {hasImages ? (
                    <>
                      <img
                        src={proxyImg(images[imgIdx])}
                        alt={product.name}
                        className="max-w-full max-h-full object-contain cursor-zoom-in"
                        style={{ maxHeight: 340 }}
                        onClick={() => setLightbox(true)}
                      />
                      {images.length > 1 && (
                        <>
                          <button onClick={prevImg}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center shadow-md"
                            style={{ background: "rgba(255,255,255,0.9)", color: "#0b1a3b" }}>
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <button onClick={nextImg}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center shadow-md"
                            style={{ background: "rgba(255,255,255,0.9)", color: "#0b1a3b" }}>
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button onClick={() => setLightbox(true)}
                        className="absolute bottom-2 right-2 w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{ background: "rgba(0,0,0,0.4)", color: "#fff" }}>
                        <ZoomIn className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-8" style={{ color: "#c5cfe3" }}>
                      <ImgIcon className="h-16 w-16" />
                      <span className="text-[11px] font-mono uppercase tracking-widest">No images yet</span>
                      <span className="text-[10px] text-center" style={{ color: "#94a3b8" }}>
                        Images will appear here once Pinnacle API delivers them
                      </span>
                    </div>
                  )}
                </div>

                {/* Thumbnail strip */}
                {images.length > 1 && (
                  <div className="flex gap-1.5 p-3 overflow-x-auto shrink-0" style={{ borderTop: "1px solid #eef2f8" }}>
                    {images.map((img, i) => (
                      <button key={i} onClick={() => setImgIdx(i)}
                        className="w-14 h-14 rounded-lg overflow-hidden shrink-0 transition-all"
                        style={{
                          border: i === imgIdx ? "2px solid #1e5bb8" : "2px solid transparent",
                          background: "#e2e8f0",
                        }}>
                        <img src={proxyImg(img)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Counter */}
                {images.length > 1 && (
                  <div className="text-center py-1.5 text-[10px] font-mono shrink-0" style={{ color: "#94a3b8" }}>
                    {imgIdx + 1} / {images.length}
                  </div>
                )}

                {/* ── Customer-facing image selection ─────────────────── */}
                {images.length > 0 && (
                  <ImageSelectionPanel
                    productId={product.id}
                    images={images}
                    featuredImage={product.featured_image || null}
                    galleryImages={product.gallery_images || []}
                    proxyImg={proxyImg}
                    adminApi={adminApi}
                    toast={toast}
                    onSaved={(fi, gi) => {
                      product.featured_image = fi;
                      product.gallery_images = gi;
                    }}
                  />
                )}

                {/* Quick stats below image */}
                <div className="p-4 space-y-3 shrink-0" style={{ borderTop: "1px solid #eef2f8" }}>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl p-3 text-center" style={{ background: "#fff", border: "1px solid #eef2f8" }}>
                      <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#94a3b8" }}>Price</div>
                      <div className="font-heading font-bold text-[18px]" style={{ color: "#0b1a3b" }}>{formatAud(product.price)}</div>
                      <div className="text-[9px] font-mono" style={{ color: "#94a3b8" }}>incl. GST</div>
                    </div>
                    <div className="rounded-xl p-3 text-center" style={{
                      background: product.stock <= 0 ? "#fee2e2" : product.stock < 3 ? "#fef3c7" : "#dcfce7",
                      border: `1px solid ${product.stock <= 0 ? "#fca5a5" : product.stock < 3 ? "#fde68a" : "#86efac"}`,
                    }}>
                      <div className="text-[10px] font-mono uppercase tracking-widest mb-1"
                        style={{ color: product.stock <= 0 ? "#991b1b" : product.stock < 3 ? "#92400e" : "#15803d" }}>
                        Stock
                      </div>
                      <div className="font-heading font-bold text-[18px]"
                        style={{ color: product.stock <= 0 ? "#991b1b" : product.stock < 3 ? "#92400e" : "#15803d" }}>
                        {product.stock}
                      </div>
                      <div className="text-[9px] font-mono"
                        style={{ color: product.stock <= 0 ? "#991b1b" : "#94a3b8" }}>
                        {product.stock <= 0 ? "out of stock" : "units"}
                      </div>
                    </div>
                  </div>

                  {product.stock <= 0 && (
                    <button
                      onClick={restock}
                      disabled={restocking}
                      className="w-full py-2.5 rounded-xl text-white font-semibold text-[13px] flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                      style={{ background: "#16a34a" }}>
                      <RefreshCcw className={`h-3.5 w-3.5 ${restocking ? "animate-spin" : ""}`} />
                      {restocking ? "Restocking…" : "Mark Back In Stock"}
                    </button>
                  )}

                  {/* Flags */}
                  <div className="space-y-1.5">
                    {[
                      { key: "is_featured",   label: "Featured on store" },
                      { key: "is_negotiable", label: "Price negotiable" },
                      { key: "is_hidden",     label: "Hidden from store" },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center justify-between cursor-pointer py-1">
                        <span className="text-[12px]" style={{ color: "#475569" }}>{label}</span>
                        <div
                          onClick={() => patchFlag(key, !product[key])}
                          className="w-9 h-5 rounded-full relative transition-colors cursor-pointer"
                          style={{ background: product[key] ? "#1e5bb8" : "#dde3ef" }}>
                          <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
                            style={{ left: product[key] ? "18px" : "2px" }} />
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Right: part details ──────────────────────────── */}
              <div className="col-span-3 overflow-y-auto p-5 space-y-5">

                {/* Status badges */}
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[10px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                    style={{ background: condColor.bg, color: condColor.fg, border: `1px solid ${condColor.bg}` }}>
                    {product.condition || "used"}
                  </span>
                  {product.is_deleted && (
                    <span className="text-[10px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                      style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" }}>
                      Invoiced — removed
                    </span>
                  )}
                  {product.is_active && !product.is_hidden && !product.is_deleted && (
                    <span className="text-[10px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                      style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" }}>
                      Live on store
                    </span>
                  )}
                  {product.sync_source === "pinnacle" && (
                    <span className="text-[10px] font-mono font-bold uppercase px-2.5 py-1 rounded-lg"
                      style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #93c5fd" }}>
                      Pinnacle
                    </span>
                  )}
                </div>

                {/* IDs row */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "SKU / Inv#", value: product.sku },
                    { label: "Tag / Barcode", value: product.barcode || "—" },
                    { label: "Stock No.", value: product.stock_no || "—" },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-xl p-3" style={{ background: "#f8fafc", border: "1px solid #eef2f8" }}>
                      <div className="text-[9px] font-mono uppercase tracking-widest mb-1" style={{ color: "#94a3b8" }}>{label}</div>
                      <div className="font-mono font-semibold text-[13px]" style={{ color: "#0b1a3b" }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Donor vehicle */}
                {(product.vehicle_make || product.vehicle_model) && (
                  <div className="rounded-xl p-4" style={{ background: "#f8fafc", border: "1px solid #eef2f8" }}>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Car className="h-3.5 w-3.5" style={{ color: "#1e5bb8" }} />
                      <span className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: "#475569" }}>
                        Donor Vehicle
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                      {[
                        { label: "Make", value: product.vehicle_make },
                        { label: "Model", value: product.vehicle_model },
                        { label: "Year", value: product.vehicle_year },
                        { label: "Odometer", value: product.odometer ? `${Number(product.odometer).toLocaleString()} km` : null },
                        { label: "VIN", value: product.donor_vin },
                        { label: "Engine", value: product.donor_engine },
                      ].filter(f => f.value).map(({ label, value }) => (
                        <div key={label}>
                          <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>{label}</div>
                          <div className="text-[12px] font-mono mt-0.5" style={{ color: "#0f172a" }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fitment years */}
                {product.fitment_years?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>
                      Compatible Years ({product.fitment_years.length})
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {product.fitment_years.map(yr => (
                        <span key={yr} className="text-[11px] font-mono px-2 py-0.5 rounded-md"
                          style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" }}>
                          {yr}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {product.long_description && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: "#94a3b8" }}>
                      Description
                    </div>
                    <pre className="text-[12px] font-mono whitespace-pre-wrap leading-relaxed"
                      style={{ color: "#475569" }}>
                      {product.long_description}
                    </pre>
                  </div>
                )}

                {/* Sync info */}
                <div className="rounded-xl p-4" style={{ background: "#f8fafc", border: "1px solid #eef2f8" }}>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>
                    Pinnacle Sync
                  </div>
                  <SyncBadge
                    status={product.sync_status || "pending"}
                    lastSyncedAt={product.last_synced_at}
                    syncError={product.sync_error}
                  />
                  {product.last_synced_at && (
                    <div className="text-[10px] font-mono mt-1.5" style={{ color: "#94a3b8" }}>
                      Last synced {new Date(product.last_synced_at).toLocaleString("en-AU")}
                    </div>
                  )}
                </div>

                {/* Tags */}
                {product.tags?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>Tags</div>
                    <div className="flex flex-wrap gap-1.5">
                      {product.tags.map(t => (
                        <span key={t} className="text-[11px] px-2.5 py-1 rounded-lg"
                          style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Image lightbox ──────────────────────────────────────── */}
      {lightbox && hasImages && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.92)" }}
          onClick={() => setLightbox(false)}>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
            onClick={e => { e.stopPropagation(); prevImg(); }}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <img src={proxyImg(images[imgIdx])} alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()} />
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
            onClick={e => { e.stopPropagation(); nextImg(); }}>
            <ChevronRight className="h-5 w-5" />
          </button>
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
            onClick={() => setLightbox(false)}>
            <X className="h-5 w-5" />
          </button>
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[12px] font-mono"
              style={{ color: "rgba(255,255,255,0.6)" }}>
              {imgIdx + 1} / {images.length}
            </div>
          )}
        </div>
      )}

      {/* ── Quick order modal ────────────────────────────────────── */}
      {orderOpen && product && (
        <DrawerOrderModal
          product={product}
          onClose={() => setOrderOpen(false)}
          onCreated={(order) => { setOrderOpen(false); onOrderCreated(order); }}
        />
      )}
    </>
  );
}

// ── BulkOrderModal — create ONE order from multiple selected inventory items ─
function BulkOrderModal({ products, onClose, onCreated }) {
  const [source,   setSource]   = useState("walkin");
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [showSug,  setShowSug]  = useState(false);
  const [payment,  setPayment]  = useState("walkin_cash");
  const [shipping, setShipping] = useState(15);
  const [notes,    setNotes]    = useState("");
  const [busy,     setBusy]     = useState(false);
  // One line per selected product — qty defaults to 1, editable, removable
  // (in case the wrong part was selected in Inventory before opening this)
  const [lines, setLines] = useState(() =>
    products.map((p) => ({ product_id: p.id, sku: p.sku, name: p.name, price: p.price, orig_price: p.price, qty: 1 }))
  );

  const searchCustomers = async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    try {
      const { data } = await adminApi.get(`/admin/customers/search?q=${encodeURIComponent(q)}&limit=6`);
      setSuggestions(data.items || []);
      setShowSug(true);
    } catch { setSuggestions([]); }
  };

  const pickCustomer = (c) => {
    setCustomer({ name: c.name, email: c.email || "", phone: c.phone || "" });
    setSuggestions([]);
    setShowSug(false);
  };

  const updateQty   = (i, qty)   => setLines((arr) => arr.map((l, j) => (j === i ? { ...l, qty: Math.max(1, Number(qty) || 1) } : l)));
  const updatePrice = (i, price) => setLines((arr) => arr.map((l, j) => (j === i ? { ...l, price: Math.max(0, Number(price) || 0) } : l)));
  const removeLine  = (i)        => setLines((arr) => arr.filter((_, j) => j !== i));

  const subtotal = lines.reduce((s, l) => s + Number(l.price || 0) * Number(l.qty || 0), 0);
  const total    = subtotal + Number(shipping || 0);

  const submit = async () => {
    if (lines.length === 0) { toast.error("No items left in this order"); return; }
    if (!customer.name.trim()) { toast.error("Customer name required"); return; }
    setBusy(true);
    try {
      const body = {
        source,
        customer_name: customer.name,
        customer_phone: customer.phone,
        items: lines.map(({ product_id, sku, name, price, qty }) => ({ product_id, sku, name, price, qty })),
        payment_method: payment,
        status: ["walkin_cash", "walkin_card"].includes(payment) ? "paid" : "confirmed",
        shipping: Number(shipping || 0),
        notes,
        fulfillment_type: "delivery",
      };
      if (customer.email) body.customer_email = customer.email;
      const { data } = await adminApi.post("/admin/orders/manual", body);
      toast.success(`Order ${data.order.order_number} created with ${lines.length} item${lines.length > 1 ? "s" : ""}`);
      onCreated(data.order);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally { setBusy(false); }
  };

  const inp = { background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" };
  const srcBtn = (active) => active
    ? { background: "#e31e24", border: "1px solid #e31e24", color: "#ffffff" }
    : { background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl"
        style={{ border: "1px solid #dde3ef" }} onClick={e => e.stopPropagation()} data-testid="bulk-order-modal">

        <div className="p-5 flex items-start justify-between sticky top-0 bg-white rounded-t-2xl z-10" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>New Order</div>
            <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>
              Create Order · {lines.length} item{lines.length !== 1 ? "s" : ""}
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} style={{ color: "#475569" }}><X className="h-4 w-4" /></Button>
        </div>

        <div className="p-5 space-y-4">
          {/* Selected items */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wide mb-2" style={{ color: "#64748b" }}>Items</div>
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #dde3ef" }}>
              {lines.map((l, i) => (
                <div key={l.product_id || i} className="p-2.5 flex items-center gap-2"
                  style={{ borderBottom: i < lines.length - 1 ? "1px solid #f0f4f8" : "none" }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium truncate" style={{ color: "#0f172a" }}>{l.name}</div>
                    <div className="text-[10px] font-mono" style={{ color: "#94a3b8" }}>
                      {l.sku}
                      {Number(l.price) !== Number(l.orig_price) && (
                        <span> · list {formatAud(l.orig_price)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-[8px] font-mono uppercase tracking-wide" style={{ color: "#94a3b8" }}>Price</span>
                    <Input type="number" min="0" step="0.01" value={l.price} onChange={(e) => updatePrice(i, e.target.value)}
                      className="w-20 rounded-lg text-xs" style={inp} data-testid={`bulk-order-price-${i}`} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-[8px] font-mono uppercase tracking-wide" style={{ color: "#94a3b8" }}>Qty</span>
                    <Input type="number" min="1" value={l.qty} onChange={(e) => updateQty(i, e.target.value)}
                      className="w-14 rounded-lg text-xs" style={inp} data-testid={`bulk-order-qty-${i}`} />
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeLine(i)} style={{ color: "#e31e24" }}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {lines.length === 0 && (
                <div className="p-4 text-center text-[11px] font-mono" style={{ color: "#94a3b8" }}>
                  All items removed — close and reselect from Inventory
                </div>
              )}
            </div>
          </div>

          {/* Source */}
          <div className="grid grid-cols-3 gap-2">
            {[{ v: "walkin", label: "Walk-in" }, { v: "phone", label: "Phone" }, { v: "trade", label: "Trade" }].map(({ v, label }) => (
              <button key={v} type="button" onClick={() => setSource(v)}
                className="py-2 rounded-xl text-[11px] font-mono uppercase tracking-widest"
                style={srcBtn(source === v)}>
                {label}
              </button>
            ))}
          </div>

          {/* Customer smart search */}
          <div className="space-y-2">
            <div className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Customer</div>
            <div className="relative">
              <input value={customer.name}
                onChange={(e) => { setCustomer((c) => ({ ...c, name: e.target.value })); searchCustomers(e.target.value); }}
                onFocus={() => customer.name.length >= 2 && setShowSug(true)}
                onBlur={() => setTimeout(() => setShowSug(false), 150)}
                placeholder="Name, phone or email to search…"
                className="w-full h-9 px-3 rounded-xl text-[13px] outline-none"
                style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }} />
              {showSug && suggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 rounded-xl max-h-40 overflow-y-auto shadow-xl bg-white" style={{ border: "1px solid #dde3ef" }}>
                  {suggestions.map((c) => (
                    <button key={c.id} onClick={() => pickCustomer(c)} type="button"
                      className="w-full px-3 py-2 text-left hover:bg-[#f5f7fb] text-[11px]">
                      <div className="font-medium" style={{ color: "#0f172a" }}>{c.name}</div>
                      <div className="font-mono" style={{ color: "#94a3b8", fontSize: 10 }}>{c.email || c.phone}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
                placeholder="Email (optional)" className="h-9 px-3 rounded-xl text-[12px] outline-none"
                style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }} />
              <input value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
                placeholder="Phone (optional)" className="h-9 px-3 rounded-xl text-[12px] outline-none"
                style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }} />
            </div>
          </div>

          {/* Payment */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Payment method</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[{ v: "walkin_cash", label: "Cash" }, { v: "walkin_card", label: "Card" }, { v: "bank_transfer", label: "Bank" }].map((m) => (
                <button key={m.v} type="button" onClick={() => setPayment(m.v)}
                  className="py-2 rounded-xl text-[11px] font-mono uppercase tracking-widest" style={srcBtn(payment === m.v)}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Shipping + total */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Shipping (AUD)</Label>
              <Input type="number" step="0.01" value={shipping} onChange={(e) => setShipping(e.target.value)} className="mt-1 rounded-xl" style={inp} />
            </div>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Total</Label>
              <div className="mt-1 h-10 px-3 rounded-xl flex items-center font-heading text-lg font-bold"
                style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#0f172a" }}>
                {formatAud(total)}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes" className="mt-1 rounded-xl" style={inp} />
          </div>

          <Button onClick={submit} disabled={busy || lines.length === 0}
            className="w-full h-11 text-white font-bold uppercase tracking-wide rounded-xl text-[13px]" style={{ background: "#e31e24" }}
            data-testid="bulk-order-submit-btn">
            {busy ? "Creating order…" : `Create order · ${formatAud(total)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── DrawerOrderModal — create order from product detail drawer ─────────
function DrawerOrderModal({ product, onClose, onCreated }) {
  const [source,   setSource]   = useState("walkin");
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [showSug,  setShowSug]  = useState(false);
  const [payment,  setPayment]  = useState("walkin_cash");
  const [shipping, setShipping] = useState(15);
  const [price,    setPrice]    = useState(product.price);
  const [notes,    setNotes]    = useState("");
  const [busy,     setBusy]     = useState(false);

  const searchCustomers = async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    try {
      const { data } = await adminApi.get(`/admin/customers/search?q=${encodeURIComponent(q)}&limit=6`);
      setSuggestions(data.items || []);
      setShowSug(true);
    } catch { setSuggestions([]); }
  };

  const pickCustomer = (c) => {
    setCustomer({ name: c.name, email: c.email || "", phone: c.phone || "" });
    setSuggestions([]);
    setShowSug(false);
  };

  const total = Number(price || 0) + Number(shipping || 0);

  const submit = async () => {
    if (!customer.name.trim()) { toast.error("Customer name required"); return; }
    setBusy(true);
    try {
      const body = {
        source,
        customer_name: customer.name,
        customer_phone: customer.phone,
        items: [{ product_id: product.id, sku: product.sku, name: product.name, price: Number(price || 0), qty: 1 }],
        payment_method: payment,
        status: ["walkin_cash", "walkin_card"].includes(payment) ? "paid" : "confirmed",
        shipping: Number(shipping || 0),
        notes,
        fulfillment_type: "delivery",
      };
      if (customer.email) body.customer_email = customer.email;
      const { data } = await adminApi.post("/admin/orders/manual", body);
      onCreated(data.order);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally { setBusy(false); }
  };

  const inp = { background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" };
  const srcBtn = (active) => active
    ? { background: "#e31e24", border: "1px solid #e31e24", color: "#ffffff" }
    : { background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
        style={{ border: "1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>

        <div className="p-5 flex items-start justify-between" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>New Order</div>
            <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>Create Order</div>
            <div className="text-[11px] mt-0.5 font-mono" style={{ color: "#64748b" }}>{product.name} · {product.sku}</div>
          </div>
          <Button variant="ghost" onClick={onClose} style={{ color: "#475569" }}><X className="h-4 w-4" /></Button>
        </div>

        <div className="p-5 space-y-4">
          {/* Price — editable in case a discount applies */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
              Price (AUD) {Number(price) !== Number(product.price) && (
                <span style={{ color: "#94a3b8" }} className="font-normal normal-case">— Pinnacle list: {formatAud(product.price)}</span>
              )}
            </Label>
            <Input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
              className="mt-1 rounded-xl" style={inp} data-testid="drawer-order-price" />
          </div>

          {/* Source */}
          <div className="grid grid-cols-3 gap-2">
            {[{ v: "walkin", label: "Walk-in" }, { v: "phone", label: "Phone" }, { v: "trade", label: "Trade" }].map(({ v, label }) => (
              <button key={v} type="button" onClick={() => setSource(v)}
                className="py-2 rounded-xl text-[11px] font-mono uppercase tracking-widest"
                style={srcBtn(source === v)}>
                {label}
              </button>
            ))}
          </div>

          {/* Customer smart search */}
          <div className="space-y-2">
            <div className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Customer</div>
            <div className="relative">
              <input value={customer.name}
                onChange={e => { setCustomer(c => ({ ...c, name: e.target.value })); searchCustomers(e.target.value); }}
                onFocus={() => customer.name.length >= 2 && setShowSug(true)}
                onBlur={() => setTimeout(() => setShowSug(false), 150)}
                placeholder="Name, phone or email to search…"
                className="w-full h-9 px-3 rounded-xl text-[13px] outline-none"
                style={{ border: "1px solid #dde3ef", ...inp }} />
              {showSug && suggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 rounded-xl shadow-xl bg-white"
                  style={{ border: "1px solid #dde3ef" }}>
                  {suggestions.map((c, i) => (
                    <button key={i} onMouseDown={() => pickCustomer(c)}
                      className="w-full px-3 py-2 text-left hover:bg-[#f5f7fb] text-[12px] flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium" style={{ color: "#0f172a" }}>{c.name}</div>
                        <div className="font-mono text-[10px]" style={{ color: "#94a3b8" }}>{c.email} · {c.phone}</div>
                      </div>
                      <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0"
                        style={{ background: "#f5f7fb", color: "#94a3b8" }}>
                        {c.source === "customer" ? "registered" : "past order"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={customer.phone} onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))}
                placeholder="Phone" className="h-9 px-3 rounded-xl text-[13px] outline-none"
                style={{ border: "1px solid #dde3ef", ...inp }} />
              <input value={customer.email} onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))}
                placeholder="Email (optional)" className="h-9 px-3 rounded-xl text-[13px] outline-none"
                style={{ border: "1px solid #dde3ef", ...inp }} />
            </div>
          </div>

          {/* Shipping + payment */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wide mb-1" style={{ color: "#64748b" }}>Shipping</div>
              <input type="number" value={shipping} onChange={e => setShipping(e.target.value)}
                className="w-full h-9 px-3 rounded-xl text-[13px] outline-none"
                style={{ border: "1px solid #dde3ef", ...inp }} />
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wide mb-1" style={{ color: "#64748b" }}>Payment</div>
              <select value={payment} onChange={e => setPayment(e.target.value)}
                className="w-full h-9 px-3 rounded-xl text-[13px] outline-none"
                style={{ border: "1px solid #dde3ef", background: "#fff", color: "#0f172a" }}>
                <option value="walkin_cash">Cash</option>
                <option value="walkin_card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cod">COD</option>
                <option value="stripe_link">Stripe Link</option>
              </select>
            </div>
          </div>

          <input value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full h-9 px-3 rounded-xl text-[13px] outline-none"
            style={{ border: "1px solid #dde3ef", ...inp }} />

          {/* Total + submit */}
          <div className="pt-2" style={{ borderTop: "1px solid #eef2f8" }}>
            <div className="flex justify-between items-center mb-3">
              <span className="text-[13px]" style={{ color: "#64748b" }}>Total</span>
              <span className="font-heading font-bold text-[20px]" style={{ color: "#0b1a3b" }}>{formatAud(total)}</span>
            </div>
            <Button onClick={submit} disabled={busy}
              className="w-full h-11 text-white font-bold uppercase tracking-wide rounded-xl"
              style={{ background: "#e31e24" }}>
              {busy ? "Creating…" : `Create Order · ${formatAud(total)}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tag Modal ──────────────────────────────────────────────────────────
function TagModal({ product, onClose, onChanged }) {
  const [tags, setTags] = useState(product.tags || []);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const addTag = async (tag) => {
    if (tags.includes(tag)) return;
    if (tags.length >= 5) { toast.error("Maximum 5 tags per product"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/products/${product.id}/tags`, { tag });
      setTags(data.tags);
      toast.success(`Tag "${tag}" added`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const removeTag = async (tag) => {
    setBusy(true);
    try {
      const { data } = await adminApi.delete(`/admin/products/${product.id}/tags/${encodeURIComponent(tag)}`);
      setTags(data.tags);
      toast.success(`Tag "${tag}" removed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const addCustom = async () => {
    const t = custom.trim();
    if (!t) return;
    if (t.length > 20) { toast.error("Max 20 characters"); return; }
    await addTag(t);
    setCustom("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>Manage Tags</div>
            <div className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>{product.name}</div>
          </div>
          <button onClick={onClose} style={{ color: "#94a3b8" }}><X className="h-5 w-5" /></button>
        </div>
        <div className="mb-4">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>
            Current tags ({tags.length}/5)
          </div>
          {tags.length === 0
            ? <div className="text-[12px] font-mono" style={{ color: "#94a3b8" }}>No tags yet</div>
            : (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span key={t}
                    className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-lg border ${TAG_STYLES[t] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
                    {t}
                    <button onClick={() => removeTag(t)} disabled={busy} className="ml-0.5 opacity-60 hover:opacity-100">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )
          }
        </div>
        <div className="mb-4">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>Quick add</div>
          <div className="flex flex-wrap gap-1.5">
            {PRESET_TAGS.map((t) => (
              <button key={t} onClick={() => addTag(t)}
                disabled={busy || tags.includes(t) || tags.length >= 5}
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-lg border transition-all ${
                  tags.includes(t)
                    ? "opacity-40 cursor-not-allowed " + (TAG_STYLES[t] || "bg-zinc-100 text-zinc-600 border-zinc-200")
                    : (TAG_STYLES[t] || "bg-zinc-100 text-zinc-600 border-zinc-200") + " hover:opacity-80 cursor-pointer"
                }`}>
                {tags.includes(t) ? "✓ " : "+ "}{t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>
            Custom tag (max 20 chars)
          </div>
          <div className="flex gap-2">
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value.slice(0, 20))}
              onKeyDown={(e) => e.key === "Enter" && addCustom()}
              placeholder="Type a custom tag…"
              className="flex-1 h-9 px-3 rounded-xl text-sm outline-none"
              style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#0f172a" }}
            />
            <span className="text-[11px] flex items-center" style={{ color: "#94a3b8" }}>{custom.length}/20</span>
            <button onClick={addCustom} disabled={busy || !custom.trim() || tags.length >= 5}
              className="h-9 px-3 rounded-xl font-bold uppercase text-[11px] text-white disabled:opacity-40"
              style={{ background: "#e31e24" }}>
              Add
            </button>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={() => onChanged()}
            className="px-4 h-9 rounded-xl text-sm font-semibold"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Action config & timeAgo ────────────────────────────────────────────
const ACTION_CONFIG = {
  tag_added:        { icon: Tag,        color: "text-blue-500",   label: "Tag added"        },
  tag_removed:      { icon: Tag,        color: "text-zinc-400",   label: "Tag removed"      },
  feature:          { icon: Star,       color: "text-amber-500",  label: "Marked featured"  },
  unfeature:        { icon: Star,       color: "text-zinc-400",   label: "Removed featured" },
  hide:             { icon: EyeOff,     color: "text-red-500",    label: "Hidden"           },
  unhide:           { icon: Eye,        color: "text-green-500",  label: "Made visible"     },
  negotiable:       { icon: DollarSign, color: "text-teal-500",   label: "Make Offer on"    },
  unnegotiable:     { icon: DollarSign, color: "text-zinc-400",   label: "Make Offer off"   },
  soft_delete:      { icon: Trash2,     color: "text-red-500",    label: "Deleted"          },
  restore:          { icon: Package,    color: "text-green-500",  label: "Restored"         },
  force_sync:       { icon: RefreshCcw, color: "text-blue-500",   label: "Force synced"     },
  price_change:     { icon: DollarSign, color: "text-amber-500",  label: "Price changed"    },
  stock_adjustment: { icon: Package,    color: "text-purple-500", label: "Stock adjusted"   },
  csv_import:       { icon: Zap,        color: "text-green-500",  label: "Imported via CSV" },
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

function ProductHistory({ productId }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!productId) return;
    adminApi.get(`/admin/products/${productId}/history`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <div className="py-8 text-center text-[12px] font-mono uppercase tracking-widest animate-pulse" style={{ color: "#94a3b8" }}>Loading history…</div>;
  if (items.length === 0) return (
    <div className="py-8 text-center">
      <Clock className="h-7 w-7 mx-auto mb-3" style={{ color: "#c5cfe3" }} />
      <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No history yet</div>
    </div>
  );

  return (
    <div className="relative pl-2">
      <div className="absolute left-7 top-0 bottom-4 w-px" style={{ background: "#eef2f8" }} />
      <div className="space-y-1">
        {items.map((entry, i) => {
          const cfg  = ACTION_CONFIG[entry.action] || {};
          const Icon = cfg.icon || Clock;
          return (
            <div key={entry.id || i} className="relative flex items-start gap-3 pb-3">
              <div className="relative z-10 w-10 flex justify-center pt-0.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
                  <Icon className={`h-3 w-3 ${cfg.color || "text-zinc-400"} shrink-0`} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-medium" style={{ color: "#0f172a" }}>
                    {cfg.label || entry.action?.replace(/_/g, " ")}
                  </span>
                  {entry.details?.tag && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md" style={{ background: "#f5f7fb", color: "#475569", border: "1px solid #dde3ef" }}>
                      {entry.details.tag}
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-mono mt-0.5 flex gap-2" style={{ color: "#94a3b8" }}>
                  <span>{entry.performed_by || "system"}</span>
                  <span style={{ color: "#dde3ef" }}>·</span>
                  <span title={new Date(entry.at).toLocaleString("en-AU")}>{timeAgo(entry.at)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Row component ──────────────────────────────────────────────────────
function Row({ p, checked, onToggle, onEbay, onFacebook, onHide, onTag, onDetail, onChanged }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const ebay       = p.ebay_listing || {};
  const ebayListed = ebay.status === "listed";
  const fbPosted   = !!p.social_posts?.facebook?.posted_at;
  const igPosted   = !!p.social_posts?.instagram?.posted_at;
  const mpPrefilled = !!p.social_posts?.marketplace?.prefilled_at;

  const postMarketplace = async () => {
    try {
      const { data } = await adminApi.post(`/admin/products/${p.id}/social-post`, { platform: "marketplace" });
      if (data.prefill_url) {
        window.open(data.prefill_url, "_blank", "noopener,noreferrer");
        toast.success("Facebook Marketplace opened — review and click Post");
        onChanged();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open Marketplace — try again");
    }
  };
  const toggleFeatured = async () => {
    await adminApi.post("/admin/products/bulk", { ids: [p.id], action: p.is_featured ? "unfeature" : "feature" });
    toast.success(p.is_featured ? "Removed from featured" : "Marked as featured");
    onChanged();
  };
const toggleNegotiable = async () => {
  try {
    await adminApi.post("/admin/products/bulk", { ids: [p.id], action: p.is_negotiable ? "unnegotiable" : "negotiable" });
    toast.success(p.is_negotiable ? "Make Offer disabled" : "Make Offer enabled for customers");
    onChanged();
  } catch (e) {
    const detail = e.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : "Action failed — check server.py BulkAction model");
  }
};
  const forceSync = async () => {
    await adminApi.post(`/admin/products/${p.id}/force-sync`);
    toast.success(`Sync queued for ${p.sku}`);
    onChanged();
  };
  const retrySync = async () => {
    const { data: qData } = await adminApi.get(`/admin/sync-queue?status=failed`);
    const event = (qData.items || []).find((e) => e.ref_id === p.id);
    if (event) {
      await adminApi.post(`/admin/sync-queue/${event.id}/retry`);
      toast.success(`Retry queued for ${p.sku}`);
    } else {
      await forceSync();
    }
    onChanged();
  };

  return (
    <>
    <tr
      className="hover:bg-[#f5f7fb] transition-colors"
      style={{
        borderBottom: "1px solid #f0f4f8",
        opacity: p.is_hidden ? 0.55 : 1,
      }}
      data-testid={`admin-product-row-${p.sku}`}
    >
      {/* Checkbox */}
      <td className="text-center px-3 py-3">
        <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`row-select-${p.sku}`} />
      </td>

      {/* Product */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0" style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
            {p.image_url
              ? <img src={proxyImg(p.image_url)} alt="" className="w-full h-full object-cover" />
              : <ImgIcon className="m-2.5 h-5 w-5" style={{ color: "#c5cfe3" }} />
            }
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate" style={{ color: "#0f172a" }}>{p.name}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest truncate mt-0.5" style={{ color: "#94a3b8" }}>
              {formatCategory(p.category)} · {p.brand}
            </div>
          </div>
        </div>
      </td>

      {/* SKU */}
      <td className="px-3 py-3">
        <span className="text-[11px] font-mono font-medium" style={{ color: "#475569" }}>{p.sku}</span>
      </td>

      {/* Vehicle */}
      <td className="px-3 py-3">
        <span className="text-[12px]" style={{ color: "#475569" }}>
          {p.vehicle_make
            ? `${p.vehicle_make} ${p.vehicle_model || ""}${p.vehicle_year ? ` · ${p.vehicle_year}` : ""}`
            : "Universal"
          }
        </span>
      </td>

      {/* Price */}
      <td className="px-3 py-3 text-right">
        <div className="font-heading font-bold text-[14px]" style={{ color: "#0f172a" }}>{formatAud(p.price)}</div>
        {p.discount_pct > 0 && (
          <div className="text-[10px] font-mono font-bold mt-0.5" style={{ color: "#16a34a" }}>-{p.discount_pct}%</div>
        )}
      </td>

      {/* Stock */}
      <td className="px-3 py-3 text-right">
        <span
          className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 rounded-lg font-mono text-[11px] font-bold"
          style={
            p.stock === 0
              ? { background: "#fee2e2", color: "#991b1b" }
              : p.stock < 20
              ? { background: "#fef3c7", color: "#92400e" }
              : { background: "#dcfce7", color: "#15803d" }
          }
        >{p.stock}</span>
      </td>

      {/* Social */}
      <td className="px-3 py-3">
        <div className="flex items-center justify-center gap-1" data-testid={`social-${p.sku}`}>
          {/* Facebook Page post — opens modal */}
          <button onClick={() => onFacebook(p)} title={fbPosted ? "Re-post to Facebook Page" : "Post to Facebook Page"}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
            style={fbPosted
              ? { background: "#1877f2", color: "#ffffff" }
              : { background: "#eff6ff", color: "#2563eb" }
            }
            data-testid={`social-fb-${p.sku}`}>
            <Facebook className="h-3 w-3" />
          </button>
          {/* Facebook Marketplace prefill — opens FB in new tab */}
          <button onClick={postMarketplace}
            title={mpPrefilled ? "Open Marketplace again (pre-filled)" : "List on Facebook Marketplace (pre-filled, you post)"}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all text-[9px] font-bold"
            style={mpPrefilled
              ? { background: "#1877f2", color: "#ffffff" }
              : { background: "#eff6ff", color: "#2563eb" }
            }
            data-testid={`social-mp-${p.sku}`}>
            <span style={{ fontSize: "8px", fontWeight: 700 }}>MP</span>
          </button>
          {/* Instagram */}
          <button onClick={() => toast.info("Instagram integration coming soon")} title="Post to Instagram (coming soon)"
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
            style={igPosted
              ? { background: "#e1306c", color: "#ffffff" }
              : { background: "#fce7f3", color: "#be185d" }
            }
            data-testid={`social-ig-${p.sku}`}>
            <Instagram className="h-3 w-3" />
          </button>
          {/* eBay */}
          <button onClick={onEbay} title={ebayListed ? `Listed at ${ebay.ebay_price}` : "List on eBay"}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
            style={ebayListed
              ? { background: "#854d0e", color: "#fef3c7" }
              : { background: "#fef9c3", color: "#92400e" }
            }
            data-testid={`social-ebay-${p.sku}`}>
            <EbayIcon />
          </button>
        </div>
      </td>

      {/* Status + Tags */}
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1 mb-1.5">
          {p.is_featured && (
            <span className="badge" style={{ background: "#fef3c7", color: "#92400e", borderColor: "#fde68a", fontSize: 9 }}>★ Featured</span>
          )}
          {ebayListed && (
            <span className="badge badge-navy" style={{ fontSize: 9 }}>eBay</span>
          )}
          {p.is_negotiable && (
            <span className="badge" style={{ background: "#ccfbf1", color: "#0f766e", borderColor: "#5eead4", fontSize: 9 }}>💬 Offer</span>
          )}
          {p.is_hidden && (
            <span className="badge badge-gray" style={{ fontSize: 9 }}>Hidden</span>
          )}
          {p.tags?.slice(0, 2).map((t) => (
            <span key={t}
              className={`badge ${TAG_STYLES[t] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}
              style={{ fontSize: 9 }}>
              #{t}
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          <button onClick={() => onTag()}
            className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-md transition-colors"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            <Tag className="h-2.5 w-2.5" /> Tags
          </button>
          <button onClick={() => setHistoryOpen(!historyOpen)}
            className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-md transition-colors"
            style={historyOpen
              ? { background: "#eff6ff", border: "1px solid #93c5fd", color: "#1e40af" }
              : { background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }
            }>
            <History className="h-2.5 w-2.5" /> History
          </button>
        </div>
      </td>

      {/* Sync — SyncBadge component UNTOUCHED */}
      <td className="px-3 py-3">
        <SyncBadge
          status={p.sync_status || (p.exported_to_pinnacle ? "synced" : "pending")}
          lastSyncedAt={p.last_synced_at}
          syncError={p.sync_error}
          onForceSync={() => forceSync()}
          onRetry={p.sync_status === "failed" ? () => retrySync() : undefined}
        />
      </td>

      {/* Actions */}
      <td className="px-3 py-3">
        <div className="flex items-center justify-end gap-1" data-testid={`row-actions-${p.sku}`}>

          {/* Detail drawer */}
          <button onClick={onDetail} title="View part detail"
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={{ background: "#0b1a3b", color: "#ffffff" }}
            data-testid={`action-detail-${p.sku}`}>
            <Package className="h-3.5 w-3.5" />
          </button>

          {/* Featured */}
          <button onClick={toggleFeatured} title={p.is_featured ? "Unfeature" : "Feature"}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={p.is_featured
              ? { background: "#fef3c7", color: "#d97706" }
              : { background: "#f5f7fb", color: "#94a3b8" }
            }
            data-testid={`action-feature-${p.sku}`}>
            <Star className={`h-3.5 w-3.5 ${p.is_featured ? "fill-current" : ""}`} />
          </button>

          {/* Preview */}
          <a href={`/product/${p.id}`} target="_blank" rel="noreferrer" title="Preview on store"
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={{ background: "#eff6ff", color: "#1e5bb8" }}
            data-testid={`action-view-${p.sku}`}>
            <Eye className="h-3.5 w-3.5" />
          </a>

          {/* Edit (Pinnacle-managed) */}
          <button title="Edit in Pinnacle" disabled
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-not-allowed"
            style={{ background: "#f5f7fb", color: "#c5cfe3" }}
            data-testid={`action-edit-${p.sku}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>

          {/* Hide/Show */}
          <button onClick={onHide} title={p.is_hidden ? "Show on website" : "Hide from website"}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={p.is_hidden
              ? { background: "#f1f5f9", color: "#475569" }
              : { background: "#f5f7fb", color: "#94a3b8" }
            }
            data-testid={`action-hide-${p.sku}`}>
            {p.is_hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>

          {/* ── NEW: Negotiate / Make Offer toggle ── */}
          <button onClick={toggleNegotiable}
            title={p.is_negotiable ? "Disable Make Offer for customers" : "Enable Make Offer for customers"}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={p.is_negotiable
              ? { background: "#ccfbf1", color: "#0f766e", boxShadow: "0 0 0 2px #5eead4" }
              : { background: "#f5f7fb", color: "#94a3b8" }
            }
            data-testid={`action-negotiate-${p.sku}`}>
            <DollarSign className="h-3.5 w-3.5" />
          </button>

        </div>
      </td>
    </tr>

    {/* History inline row */}
    {historyOpen && (
      <tr>
        <td colSpan={10} className="px-5 pb-4" style={{ borderBottom: "1px solid #f0f4f8", background: "#fafbfd" }}>
          <div className="rounded-xl p-4 mt-1" style={{ border: "1px solid #dde3ef" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: "#94a3b8" }}>
              <History className="h-3 w-3" /> History ·
              <span style={{ color: "#0f172a" }}>{p.name}</span>
            </div>
            <ProductHistory productId={p.id} />
          </div>
        </td>
      </tr>
    )}
    </>
  );
}

// ─── BulkActionModal — includes Negotiable in Visibility group ─────────
const ACTION_GROUPS = [
  {
    title: "Pricing", items: [
      { key: "discount_pct",      label: "Apply discount (%)",      icon: Percent,     hint: "Show as discount on customer website" },
      { key: "discount_fixed",    label: "Apply discount ($)",      icon: Percent },
      { key: "markup_ebay_pct",   label: "Set eBay markup (%)",     icon: ShoppingCart },
      { key: "markup_ebay_fixed", label: "Set eBay markup ($)",     icon: ShoppingCart },
    ],
  },
  {
    title: "Visibility", items: [
      { key: "feature",      label: "Mark as Featured",    icon: Star,       hint: "Shows in Featured Products on storefront" },
      { key: "unfeature",    label: "Remove Featured",     icon: Star },
      { key: "hide",         label: "Hide from website",   icon: EyeOff,     hint: "Customers can't see them — Pinnacle stock untouched" },
      { key: "unhide",       label: "Show on website",     icon: Eye },
    ],
  },
  {
    title: "Make Offer (Negotiable)", items: [
      { key: "negotiable",   label: "Enable Make Offer",   icon: DollarSign, hint: "Customers can submit price offers on selected products" },
      { key: "unnegotiable", label: "Disable Make Offer",  icon: DollarSign, hint: "Remove the Make Offer button from selected products" },
    ],
  },
  {
    title: "Channels", items: [
      { key: "list_ebay",      label: "List on eBay (with markup)", icon: ShoppingCart },
      { key: "post_facebook",  label: "Post to Facebook",           icon: Facebook },
      { key: "post_instagram", label: "Post to Instagram",          icon: Instagram },
    ],
  },
  {
    title: "Tags", items: [
      { key: "tag_add",    label: "Add a tag",    icon: Tag, hint: "Tag shows on customer website (e.g. clearance, new, special)" },
      { key: "tag_remove", label: "Remove a tag", icon: Tag },
    ],
  },
];

function BulkActionModal({ ids, products, onClose, onDone }) {
  const [step,   setStep]   = useState(1);
  const [action, setAction] = useState(null);
  const [value,  setValue]  = useState("");
  const [tag,    setTag]    = useState("");
  const [busy,   setBusy]   = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      if (action === "list_ebay") {
        await Promise.all(ids.map((id) => adminApi.post(`/admin/products/${id}/ebay-listing`, { markup: Number(value || 0), markup_mode: "pct" })));
        toast.success(`${ids.length} products listed on eBay`);
      } else if (action === "post_facebook" || action === "post_instagram") {
        const platform = action === "post_facebook" ? "facebook" : "instagram";
        await Promise.all(ids.map((id) => adminApi.post(`/admin/products/${id}/social-post`, { platform })));
        toast.success(`Posted ${ids.length} products to ${platform}`);
      } else {
        const body = { ids, action };
        if (["discount_pct","discount_fixed","markup_ebay_pct","markup_ebay_fixed"].includes(action)) body.value = Number(value || 0);
        if (["tag_add","tag_remove"].includes(action)) body.tag = tag;
        const { data } = await adminApi.post("/admin/products/bulk", body);
        toast.success(`Updated ${data.updated} products`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const needsValue = ["discount_pct","discount_fixed","markup_ebay_pct","markup_ebay_fixed","list_ebay"].includes(action);
  const needsTag   = ["tag_add","tag_remove"].includes(action);
  const valueLabel = action === "discount_pct" ? "Discount %" : action === "discount_fixed" ? "Discount $" : action === "markup_ebay_pct" ? "Markup %" : action === "markup_ebay_fixed" ? "Markup $" : action === "list_ebay" ? "Markup % (over list price)" : "Value";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={(e) => e.stopPropagation()} data-testid="bulk-action-modal">
        <div className="p-5 sticky top-0 bg-white rounded-t-2xl flex items-start justify-between" style={{ borderBottom: "1px solid #eef2f8" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Bulk action · Step {step} of 2</div>
            <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>{ids.length} product{ids.length !== 1 ? "s" : ""} selected</div>
          </div>
          <Button variant="ghost" type="button" onClick={onClose} style={{ color: "#475569" }}><X className="h-4 w-4" /></Button>
        </div>

        {step === 1 ? (
          <div className="p-5 space-y-4">
            <div className="text-[13px]" style={{ color: "#475569" }}>What would you like to do?</div>
            {ACTION_GROUPS.map((g) => (
              <div key={g.title}>
                <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>{g.title}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {g.items.map((it) => (
                    <button key={it.key} onClick={() => { setAction(it.key); setStep(2); }}
                      data-testid={`bulk-pick-${it.key}`}
                      className="text-left p-3 rounded-xl transition-all flex items-start gap-3"
                      style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
                      <it.icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#1e5bb8" }} />
                      <div>
                        <div className="text-[13px] font-medium" style={{ color: "#0f172a" }}>{it.label}</div>
                        {it.hint && <div className="text-[11px] mt-0.5" style={{ color: "#64748b" }}>{it.hint}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <button onClick={() => { setStep(1); setAction(null); setValue(""); setTag(""); }}
              className="text-[12px] font-mono uppercase tracking-widest hover:underline" style={{ color: "#94a3b8" }}
              data-testid="bulk-back-btn">
              ← Pick a different action
            </button>
            <div className="rounded-xl p-3 text-[13px]" style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
              <div className="font-heading font-bold uppercase" style={{ color: "#0f172a" }}>
                {ACTION_GROUPS.flatMap((g) => g.items).find((i) => i.key === action)?.label}
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>
                Will apply to <b>{ids.length}</b> product{ids.length !== 1 ? "s" : ""}
              </div>
            </div>
            {needsValue && (
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>{valueLabel}</Label>
                <Input type="number" step="0.01" autoFocus value={value} onChange={(e) => setValue(e.target.value)}
                  className="mt-1 rounded-xl"
                  style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
                  required data-testid="bulk-value-input" />
              </div>
            )}
            {needsTag && (
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Tag</Label>
                <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. clearance, new, special"
                  className="mt-1 rounded-xl"
                  style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
                  required data-testid="bulk-tag-input" />
              </div>
            )}
            <div className="rounded-xl overflow-hidden max-h-40 overflow-y-auto" style={{ border: "1px solid #dde3ef" }}>
              {products.slice(0, 6).map((p) => (
                <div key={p.id} className="p-2 flex items-center gap-2 text-[12px]" style={{ borderBottom: "1px solid #f0f4f8" }}>
                  <span className="font-mono" style={{ color: "#94a3b8" }}>{p.sku}</span>
                  <span className="truncate flex-1" style={{ color: "#0f172a" }}>{p.name}</span>
                  <span style={{ color: "#64748b" }}>{formatAud(p.price)}</span>
                </div>
              ))}
              {products.length > 6 && (
                <div className="p-2 text-[11px] text-center" style={{ color: "#94a3b8" }}>+ {products.length - 6} more</div>
              )}
            </div>
            <Button onClick={apply} disabled={busy || (needsValue && value === "") || (needsTag && !tag)}
              className="w-full text-white font-bold uppercase tracking-wide rounded-xl h-11"
              style={{ background: "#e31e24" }}
              data-testid="bulk-confirm-btn">
              {busy ? "Applying…" : `Apply to ${ids.length} product${ids.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FacebookModal({ product, onClose, onDone }) {
  const social  = product.social_posts || {};
  const fbPost  = social.facebook || {};
  const mpPost  = social.marketplace || {};

  const [customMessage,   setCustomMessage]   = useState("");
  const [submitting,      setSubmitting]       = useState(false);
  const [copiedField,     setCopiedField]      = useState(null);
  const [selectedImages,  setSelectedImages]   = useState([]);
  const [activeTab,       setActiveTab]        = useState("page"); // "page" | "marketplace"

  // ── Derived listing fields ───────────────────────────────────────────
  const make  = (product.vehicle_make  || "").replace(/\b\w/g, c => c.toUpperCase());
  const model = (product.vehicle_model || "").toUpperCase();
  const year  = product.vehicle_year   || "";
  const part  = (product.category || product.name || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const sku   = product.sku            || "";
  const odo   = product.odometer && product.odometer !== "999999" ? `${product.odometer} kms` : "";

  const vehicle   = [year, make, model].filter(Boolean).join(" ");
  const mpTitle   = `${vehicle ? `${vehicle} — ` : ""}${part}`.trim();
  const mpPrice   = product.price ? String(Math.round(product.price)) : "";
  const mpDesc    = [
    product.yard_comments || "",
    "✅ 6 Month Warranty | Australia-wide Shipping",
    "📞 1800 650 650 | bcpcarparts.com.au",
    sku ? `View online: https://bcpcarparts.com.au/products/${sku}` : "",
    product.stock_no ? `Stock No: ${product.stock_no}` : "",
  ].filter(Boolean).join("\n");

  const autoMessage = useMemo(() => [
    `🔧 ${vehicle ? `${vehicle} — ${part}` : part}`,
    mpPrice ? `💰 Price: $${mpPrice} AUD` : "",
    odo ? `📍 Mileage: ${odo}` : "",
    product.stock_no ? `🏷️ Stock No: ${product.stock_no}` : "",
    product.yard_comments ? `\n${product.yard_comments}` : "",
    "\n✅ 6 Month Warranty | Fast Australia-wide Shipping",
    "📞 Call: 1800 650 650",
    "🌐 Order online: bcpcarparts.com.au",
    sku ? `\n🔗 View this part: https://bcpcarparts.com.au/products/${sku}` : "",
    "\n#BrisbaneCarParts #UsedCarParts #AutoParts #Queensland #CarWreckers",
  ].filter(Boolean).join("\n"), [product, vehicle, part, mpPrice, odo, sku]);

  // ── Copy to clipboard helper ─────────────────────────────────────────
  const copyField = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  // ── Image helpers ────────────────────────────────────────────────────
  const toggleImage = (url) => {
    setSelectedImages(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const downloadImage = async (url, index) => {
    try {
      const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
      const r = await fetch(proxyUrl);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `bcp-part-${product.sku || "image"}-${index + 1}.jpg`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Download failed — right-click the image and Save As instead");
    }
  };

  // ── Page post ────────────────────────────────────────────────────────
  const postToPage = async () => {
    setSubmitting(true);
    try {
      await adminApi.post(`/admin/products/${product.id}/social-post`, {
        platform: "facebook",
        custom_message: customMessage.trim() || null,
      });
      toast.success("Posted to Facebook Page!");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post — check FB_PAGE_TOKEN in .env");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Marketplace open ─────────────────────────────────────────────────
  const openMarketplace = async () => {
    try {
      await adminApi.post(`/admin/products/${product.id}/social-post`, { platform: "marketplace" });
      window.open("https://www.facebook.com/marketplace/create/item", "_blank", "noopener,noreferrer");
      toast.success("Facebook Marketplace opened — use the copy buttons to paste details");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open Marketplace");
    }
  };

  // ── CopyRow component ────────────────────────────────────────────────
  const CopyRow = ({ label, value, fieldKey, multiline }) => (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #dde3ef" }}>
      <div className="flex items-center justify-between px-3 py-1"
        style={{ background: "#f5f7fb", borderBottom: "1px solid #dde3ef" }}>
        <span className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>{label}</span>
        <button onClick={() => copyField(value, fieldKey)}
          className="text-[10px] font-mono px-2 py-0.5 rounded-lg transition-all"
          style={{
            background: copiedField === fieldKey ? "#dcfce7" : "#ffffff",
            color: copiedField === fieldKey ? "#16a34a" : "#475569",
            border: "1px solid #dde3ef",
          }}>
          {copiedField === fieldKey ? "✓ Copied!" : "Copy"}
        </button>
      </div>
      {multiline
        ? <pre className="px-3 py-2 text-xs whitespace-pre-wrap" style={{ color: "#0f172a", fontFamily: "inherit", margin: 0 }}>{value}</pre>
        : <div className="px-3 py-2 text-sm font-medium" style={{ color: "#0f172a" }}>{value}</div>
      }
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl max-h-[92vh] flex flex-col"
        style={{ border: "1px solid #dde3ef" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Facebook</div>
            <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>{product.name}</div>
            <div className="text-[11px] font-mono" style={{ color: "#64748b" }}>{formatAud(product.price)}</div>
          </div>
          <Button variant="ghost" type="button" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pb-3">
          {[["page", "📢 Page Post"], ["marketplace", "🛒 Marketplace"]].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className="flex-1 py-2 rounded-xl text-sm font-bold transition-all"
              style={{
                background: activeTab === key ? "#1877f2" : "#f5f7fb",
                color: activeTab === key ? "#ffffff" : "#475569",
                border: "1px solid " + (activeTab === key ? "#1877f2" : "#dde3ef"),
              }}>
              {label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 px-5 pb-3 space-y-3">

          {/* ── TAB: Page Post ── */}
          {activeTab === "page" && <>
            <div className="rounded-xl p-3 text-xs" style={{ background: "#fef3c7", border: "1px solid #fcd34d" }}>
              ⏳ Waiting on Facebook Page token — contact BCP admin to add <code>FB_PAGE_TOKEN</code> to .env
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                Post message (edit before posting)
              </Label>
              <textarea
                value={customMessage || autoMessage}
                onChange={e => setCustomMessage(e.target.value)}
                rows={9}
                className="mt-1 w-full rounded-xl border px-3 py-2 text-xs resize-none"
                style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a", fontFamily: "inherit" }}
              />
            </div>
            <Button onClick={postToPage} disabled={submitting}
              className="w-full font-bold rounded-xl disabled:opacity-50"
              style={{ background: "#1877f2", color: "#ffffff" }}>
              {submitting ? "Posting…" : fbPost.posted_at ? "Re-post to Facebook Page" : "Post to Facebook Page"}
            </Button>
            {fbPost.post_url && (
              <a href={fbPost.post_url} target="_blank" rel="noreferrer"
                className="block text-center text-[11px] font-mono underline" style={{ color: "#1877f2" }}>
                View last post ↗
              </a>
            )}
          </>}

          {/* ── TAB: Marketplace ── */}
          {activeTab === "marketplace" && <>

            {/* Instructions banner */}
            <div className="rounded-xl p-3 text-xs space-y-1" style={{ background: "#f0f7ff", border: "1px solid #bfdbfe" }}>
              <div className="font-bold" style={{ color: "#1877f2" }}>How to use:</div>
              <div style={{ color: "#1e40af" }}>
                1. Click <strong>"Open Marketplace"</strong> below — opens Facebook in a new tab<br/>
                2. Use the <strong>Copy</strong> buttons to paste each field into Marketplace<br/>
                3. For images — drag them directly from the grid below into Marketplace's photo upload area, or download and upload manually
              </div>
            </div>

            {/* Open Marketplace button */}
            <Button onClick={openMarketplace}
              className="w-full font-bold rounded-xl"
              style={{ background: "#1877f2", color: "#ffffff" }}>
              🛒 Open Facebook Marketplace
            </Button>

            {/* Copy fields */}
            <div className="space-y-2">
              <CopyRow label="Title" value={mpTitle} fieldKey="title" />
              <CopyRow label="Price (AUD)" value={mpPrice} fieldKey="price" />
              <CopyRow label="Condition" value="Used - Good" fieldKey="condition" />
              <CopyRow label="Category" value="Vehicle Parts & Accessories" fieldKey="category" />
              <CopyRow label="Location" value="Richlands, QLD 4077" fieldKey="location" />
              {odo && <CopyRow label="Mileage" value={odo} fieldKey="odo" />}
              <CopyRow label="Description" value={mpDesc} fieldKey="desc" multiline />
            </div>

            {/* Image grid */}
            {(product.images || []).length > 0 && <>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wide mb-2" style={{ color: "#64748b" }}>
                  Images — drag directly into Marketplace, or download
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(product.images || []).map((url, i) => {
                    const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
                    const sel = selectedImages.includes(url);
                    return (
                      <div key={i} className="relative rounded-xl overflow-hidden group"
                        style={{ border: `2px solid ${sel ? "#1877f2" : "#dde3ef"}`, aspectRatio: "1" }}>
                        <img
                          src={proxyUrl}
                          alt={`Part ${i + 1}`}
                          draggable
                          className="w-full h-full object-cover cursor-grab active:cursor-grabbing"
                          onClick={() => toggleImage(url)}
                          onError={e => { e.target.style.display = "none"; }}
                        />
                        {/* Download button — shows on hover */}
                        <button
                          onClick={() => downloadImage(url, i)}
                          className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg px-1.5 py-0.5 text-[9px] font-bold"
                          style={{ background: "rgba(0,0,0,0.7)", color: "#ffffff" }}>
                          ⬇ Save
                        </button>
                        {sel && (
                          <div className="absolute top-1 left-1 rounded-full w-4 h-4 flex items-center justify-center"
                            style={{ background: "#1877f2" }}>
                            <span className="text-white text-[8px] font-bold">✓</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {selectedImages.length > 0 && (
                  <button
                    onClick={() => selectedImages.forEach((url, i) => downloadImage(url, i))}
                    className="mt-2 w-full py-2 rounded-xl text-xs font-bold"
                    style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
                    ⬇ Download {selectedImages.length} selected image{selectedImages.length > 1 ? "s" : ""}
                  </button>
                )}
              </div>
            </>}

          </>}
        </div>

        <div className="p-4 pt-2 border-t" style={{ borderColor: "#eef1f6" }}>
          <p className="text-[10px] text-center" style={{ color: "#94a3b8" }}>
            Page post needs FB_PAGE_TOKEN · Marketplace works now · Images draggable once deployed
          </p>
        </div>
      </div>
    </div>
  );
}


function EbayModal({ product, onClose, onDone }) {
  const ebay = product.ebay_listing || {};
  const [step, setStep] = useState(1); // 1=Pricing+Title, 2=Details, 3=Policy & Images

  // Auto-generate the eBay title from product data (matches backend build_title logic)
  const autoTitle = useMemo(() => {
    const parts = [
      (product.vehicle_make || "").toUpperCase(),
      (product.vehicle_model || "").toUpperCase(),
      product.vehicle_year || "",
      (product.category || product.name || "").replace(/_/g, " ").toUpperCase(),
      product.ebay_body_type || "",
      product.ebay_color || "",
    ].filter(Boolean).join(" ");
    return parts.slice(0, 80);
  }, [product]);

  // Step 1 — Pricing + Title
  const [markup,      setMarkup]      = useState(ebay.markup ?? 25);
  const [mode,        setMode]        = useState(ebay.markup_mode || "pct");
  const [customTitle, setCustomTitle] = useState(ebay.custom_title || autoTitle);

  // Step 2 — Listing details
  const [color,       setColor]       = useState(product.ebay_color || "");
  const [bodyType,    setBodyType]    = useState(product.ebay_body_type || "");
  const [paintCode,   setPaintCode]   = useState(product.ebay_paint_code || "");
  const [sellerNotes, setSellerNotes] = useState(product.yard_comments || "");
  const [subtitle,    setSubtitle]    = useState("");
  const [weightKg,    setWeightKg]    = useState(product.package_weight_kg ?? "");
  const [lengthCm,    setLengthCm]    = useState(product.package_length_cm ?? "");
  const [widthCm,     setWidthCm]     = useState(product.package_width_cm ?? "");
  const [heightCm,    setHeightCm]    = useState(product.package_height_cm ?? "");

  // Step 3 — Policy & Images
  const [warrantyOption, setWarrantyOption] = useState(ebay.warranty_option || "6_months");
  const [returnsOption,  setReturnsOption]  = useState(ebay.returns_option  || "no_returns");
  const [selectedImages, setSelectedImages] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  const newPrice = useMemo(() =>
    mode === "pct"
      ? +(product.price * (1 + markup / 100)).toFixed(2)
      : +(product.price + Number(markup)).toFixed(2),
  [product.price, markup, mode]);

  const toggleImage = (url) => {
    setSelectedImages(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await adminApi.post(`/admin/products/${product.id}/ebay-listing`, {
        markup: Number(markup),
        markup_mode: mode,
        custom_title: customTitle.trim() || null,
        ebay_color: color || null,
        ebay_body_type: bodyType || null,
        ebay_paint_code: paintCode || null,
        seller_notes: sellerNotes || null,
        subtitle: subtitle || null,
        weight_kg: weightKg === "" ? null : Number(weightKg),
        length_cm: lengthCm === "" ? null : Number(lengthCm),
        width_cm:  widthCm  === "" ? null : Number(widthCm),
        height_cm: heightCm === "" ? null : Number(heightCm),
        warranty_option: warrantyOption,
        returns_option: returnsOption,
        selected_images: selectedImages,
      });
      toast.success(ebay.status === "listed" ? "eBay listing updated" : "Listed on eBay");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't list on eBay — try again");
      setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  const unlist = async () => {
    try {
      await adminApi.delete(`/admin/products/${product.id}/ebay-listing`);
      toast.success("Unlisted from eBay");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't unlist — try again");
    }
  };

  const stepLabel = ["Pricing & Title", "Listing Details", "Policy & Images"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[92vh] flex flex-col"
        style={{ border: "1px solid #dde3ef" }}
        onClick={e => e.stopPropagation()}
        data-testid="ebay-modal">

        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>eBay listing — Step {step} of 3: {stepLabel[step-1]}</div>
            <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>{product.name}</div>
            <div className="text-[11px] font-mono" style={{ color: "#64748b" }}>List: {formatAud(product.price)}</div>
          </div>
          <Button variant="ghost" type="button" onClick={onClose} style={{ color: "#475569" }}><X className="h-4 w-4" /></Button>
        </div>

        {/* Step indicators */}
        <div className="flex gap-1 px-5 pb-3">
          {[1,2,3].map(s => (
            <div key={s} className="flex-1 h-1 rounded-full" style={{ background: s <= step ? "#e31e24" : "#dde3ef" }} />
          ))}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 pb-3 space-y-3">

          {/* Step 1 — Pricing + Title */}
          {step === 1 && <>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                eBay Title <span style={{ color: "#94a3b8" }}>({customTitle.length}/80)</span>
              </Label>
              <Input
                value={customTitle}
                onChange={e => setCustomTitle(e.target.value.slice(0, 80))}
                maxLength={80}
                className="mt-1 rounded-xl font-mono text-sm"
                style={{ background: "#f5f7fb", borderColor: customTitle.length > 75 ? "#e31e24" : "#dde3ef", color: "#0f172a" }}
                placeholder="Auto-generated from product data — edit if needed"
              />
              <div className="text-[10px] mt-1" style={{ color: "#94a3b8" }}>
                Pre-filled from make/model/year/category. Edit anything before listing.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Markup</Label>
                <Input type="number" step="0.01" value={markup} onChange={e => setMarkup(e.target.value)}
                  className="mt-1 rounded-xl" style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
                  data-testid="ebay-markup-input" />
              </div>
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Mode</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="mt-1 rounded-xl" style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pct">Percent (%)</SelectItem>
                    <SelectItem value="fixed">Fixed (AUD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>eBay price</div>
              <div className="font-heading text-3xl font-bold mt-1" style={{ color: "#0f172a" }}>{formatAud(newPrice)}</div>
            </div>
          </>}

          {/* Step 2 — Listing Details */}
          {step === 2 && <>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Seller Notes (shown prominently on listing)</Label>
              <textarea value={sellerNotes} onChange={e => setSellerNotes(e.target.value)}
                rows={3} maxLength={1000}
                placeholder="e.g. F20, HATCH, M SPORT, NON SEAT ADJ, 06/11-05/19"
                className="mt-1 w-full rounded-xl border px-3 py-2 text-sm resize-none"
                style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }} />
              <div className="text-[10px] text-right" style={{ color: "#94a3b8" }}>{sellerNotes.length}/1000</div>
            </div>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Subtitle <span style={{ color: "#94a3b8" }}>(optional — extra eBay fee applies)</span></Label>
              <Input value={subtitle} onChange={e => setSubtitle(e.target.value.slice(0,55))}
                placeholder="e.g. NON AUTO DIP, 06/11-05/19" maxLength={55}
                className="mt-1 rounded-xl" style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }} />
              <div className="text-[10px] text-right" style={{ color: "#94a3b8" }}>{subtitle.length}/55</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Color</Label>
                <Input value={color} onChange={e => setColor(e.target.value)} placeholder="e.g. Silver"
                  className="mt-1 rounded-xl text-sm" style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }} />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Body Type</Label>
                <Input value={bodyType} onChange={e => setBodyType(e.target.value)} placeholder="e.g. Hatchback"
                  className="mt-1 rounded-xl text-sm" style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }} />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Paint Code</Label>
                <Input value={paintCode} onChange={e => setPaintCode(e.target.value)} placeholder="0"
                  className="mt-1 rounded-xl text-sm" style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }} />
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#94a3b8" }}>
                Package dimensions (optional — blank uses this category's average)
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[["Weight (kg)", weightKg, setWeightKg, "0.1"], ["Length (cm)", lengthCm, setLengthCm, "1"], ["Width (cm)", widthCm, setWidthCm, "1"], ["Height (cm)", heightCm, setHeightCm, "1"]].map(([label, val, setter, step]) => (
                  <div key={label}>
                    <Label className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>{label}</Label>
                    <Input type="number" step={step} min="0" value={val} onChange={e => setter(e.target.value)}
                      className="mt-1 rounded-xl text-sm" style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }} />
                  </div>
                ))}
              </div>
            </div>
          </>}

          {/* Step 3 — Policy & Images */}
          {step === 3 && <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide mb-1 block" style={{ color: "#64748b" }}>Warranty Policy</Label>
                <div className="space-y-1">
                  {[["3_months", "3 Months"], ["6_months", "6 Months (default)"], ["12_months", "12 Months"]].map(([val, label]) => (
                    <label key={val} className="flex items-center gap-2 cursor-pointer p-2 rounded-xl text-sm"
                      style={{ background: warrantyOption === val ? "#f0f4ff" : "#f5f7fb", border: `1px solid ${warrantyOption === val ? "#1e5bb8" : "#dde3ef"}` }}>
                      <input type="radio" name="warranty" value={val} checked={warrantyOption === val} onChange={() => setWarrantyOption(val)} className="accent-blue-700" />
                      <span style={{ color: "#0f172a" }}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide mb-1 block" style={{ color: "#64748b" }}>Returns Policy</Label>
                <div className="space-y-1">
                  {[["no_returns", "No Returns (current)"], ["30_days_buyer_pays", "30 Days – Buyer Pays"], ["30_days_free", "30 Days – Free Returns"]].map(([val, label]) => (
                    <label key={val} className="flex items-center gap-2 cursor-pointer p-2 rounded-xl text-sm"
                      style={{ background: returnsOption === val ? "#f0f4ff" : "#f5f7fb", border: `1px solid ${returnsOption === val ? "#1e5bb8" : "#dde3ef"}` }}>
                      <input type="radio" name="returns" value={val} checked={returnsOption === val} onChange={() => setReturnsOption(val)} className="accent-blue-700" />
                      <span style={{ color: "#0f172a" }}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                Select Images <span style={{ color: "#94a3b8" }}>({selectedImages.length} selected — images go live once deployed)</span>
              </Label>
              {(product.images || []).length === 0
                ? <p className="text-sm mt-2" style={{ color: "#94a3b8" }}>No images on file for this product.</p>
                : <div className="grid grid-cols-4 gap-2 mt-2">
                    {(product.images || []).map((url, i) => {
                      const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
                      const selected = selectedImages.includes(url);
                      return (
                        <div key={i} onClick={() => toggleImage(url)}
                          className="relative rounded-xl overflow-hidden cursor-pointer"
                          style={{ border: `2px solid ${selected ? "#e31e24" : "#dde3ef"}`, aspectRatio: "1" }}>
                          <img src={proxyUrl} alt={`Part image ${i+1}`}
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display="none"; }} />
                          {selected && (
                            <div className="absolute inset-0 flex items-center justify-center"
                              style={{ background: "rgba(227,30,36,0.15)" }}>
                              <div className="rounded-full w-5 h-5 flex items-center justify-center" style={{ background: "#e31e24" }}>
                                <span className="text-white text-xs font-bold">✓</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
              }
            </div>
          </>}
        </div>

        {/* Footer nav */}
        <div className="p-5 pt-3 border-t space-y-2" style={{ borderColor: "#eef1f6" }}>
          <div className="flex gap-2">
            {step > 1 && (
              <Button type="button" onClick={() => setStep(s => s - 1)} className="rounded-xl"
                style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
                Back
              </Button>
            )}
            {step < 3
              ? <Button type="button" onClick={() => setStep(s => s + 1)} className="flex-1 font-bold uppercase tracking-wide rounded-xl"
                  style={{ background: "#0b1a3b", color: "#ffffff" }}>
                  Next →
                </Button>
              : <Button type="button" onClick={submit} disabled={submitting}
                  className="flex-1 text-white font-bold uppercase tracking-wide rounded-xl disabled:opacity-50"
                  style={{ background: "#e31e24" }}
                  data-testid="ebay-submit-btn">
                  {submitting ? "Listing…" : ebay.status === "listed" ? "Update listing" : "List on eBay"}
                </Button>
            }
            {ebay.status === "listed" && step === 1 && (
              <Button type="button" onClick={unlist} className="rounded-xl"
                style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
                Unlist
              </Button>
            )}
          </div>
          {ebay.status === "listed" && ebay.listing_url && (
            <a href={ebay.listing_url} target="_blank" rel="noreferrer"
              className="block text-center text-[11px] font-mono underline" style={{ color: "#1e5bb8" }}>
              View live listing ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel = "Confirm", onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={(e) => e.stopPropagation()} data-testid="confirm-modal">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 shrink-0 mt-0.5" style={{ color: "#d97706" }} />
          <div className="flex-1">
            <div className="font-heading text-lg font-bold" style={{ color: "#0f172a" }}>{title}</div>
            <div className="text-[13px] mt-2" style={{ color: "#475569" }}>{message}</div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose} className="rounded-xl" style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }} data-testid="confirm-cancel">Cancel</Button>
          <Button onClick={onConfirm} className="rounded-xl text-white" style={{ background: "#e31e24" }} data-testid="confirm-ok">{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}